import React, { useState, useCallback, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    TextField, 
    Button, 
    Grid, 
    Paper, 
    Box, 
    CircularProgress,
    Autocomplete,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ListItem,
    ListItemText,
    List,
    IconButton,
    Divider,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import { useDataContext } from '../DataContext'; // Import useDataContext
import { req, uploadImage, log } from '../utils'; // Import req, uploadImage and log
import HistoryIcon from '@mui/icons-material/History'; // <-- Import HistoryIcon
import MachineHistoryDrawer from '../components/MachineHistoryDrawer'; // <-- Import Drawer Component

// Removed sampleMachineIds

// LocalStorage key for the selected warehouse
const WAREHOUSE_STORAGE_KEY = 'selectedWarehouseId';

export default function MachineIntake() {
    const { 
        machines, isLoadingMachines, machineError, 
        warehouses, isLoadingWarehouses, warehouseError,
        updateMachineStatus
    } = useDataContext();
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [machineData, setMachineData] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState('');
    const [conditionNotes, setConditionNotes] = useState('');
    const [photos, setPhotos] = useState([]);
    const [uploadedPhotoPaths, setUploadedPhotoPaths] = useState([]);
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
    const [photoUploadErrors, setPhotoUploadErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMachineData, setIsLoadingMachineData] = useState(false);
    const [formError, setFormError] = useState('');

    // Drawer State - Ensure all states are present
    const [drawerMachinePk, setDrawerMachinePk] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerContent, setDrawerContent] = useState(null); // 'history' or 'notes'
    const [drawerData, setDrawerData] = useState(null);
    const [isLoadingDrawer, setIsLoadingDrawer] = useState(false);
    const [drawerMachineLabel, setDrawerMachineLabel] = useState(''); // <-- Add state for label

    // --- Restore / reconcile warehouse selection (string IDs match MUI Select values) ---
    useEffect(() => {
        if (isLoadingWarehouses) return;

        const savedWarehouseId = localStorage.getItem(WAREHOUSE_STORAGE_KEY);
        if (!savedWarehouseId) return;

        const match = warehouses.find(
            (wh) => String(wh.venue_id) === String(savedWarehouseId)
        );
        if (match) {
            setSelectedWarehouse(String(match.venue_id));
        } else {
            // Stale localStorage value (wrong type or deleted warehouse)
            localStorage.removeItem(WAREHOUSE_STORAGE_KEY);
            setSelectedWarehouse('');
        }
    }, [warehouses, isLoadingWarehouses]);

    // --- Save warehouse to localStorage on change ---
    useEffect(() => {
        if (selectedWarehouse) { // Only save if a warehouse is actually selected
            localStorage.setItem(WAREHOUSE_STORAGE_KEY, String(selectedWarehouse));
        }
    }, [selectedWarehouse]);

    // --- Handle Machine Selection ---
    const handleMachineSelect = (event, newValue) => {
        setSelectedMachine(newValue);
        setMachineData(null);
        setFormError('');
        // Reset intake form fields when machine changes
        setConditionNotes('');
        setPhotos([]);
        setUploadedPhotoPaths([]);
        setPhotoUploadErrors({});
        if (newValue) {
            // Use PK for fetching data
            fetchMachineData(newValue.pk); 
        } else {
            setIsLoadingMachineData(false); // Stop loading if deselected
        }
    };

    const handleWarehouseChange = (event) => {
        setSelectedWarehouse(String(event.target.value));
    };

    // --- Load machine details from already-fetched context (no network / no timer) ---
    const fetchMachineData = (machinePk) => {
        if (!machinePk) {
            setIsLoadingMachineData(false);
            return;
        }
        setIsLoadingMachineData(true);
        setFormError('');
        setMachineData(null);
        try {
            console.log(`Intake - Loading details for Machine PK: ${machinePk}`);
            const machineFromContext = machines.find(
                (m) => String(m.machine_id_pk) === String(machinePk)
            );

            if (!machineFromContext) {
                setFormError('Could not find details for that machine. Try refreshing the page.');
                setMachineData(null);
                log('intake_machine_not_in_context', {
                    machinePk,
                    machinesLoaded: machines.length,
                });
                return;
            }

            setMachineData({
                id: machineFromContext.serial_id || 'Unknown ID',
                pk: machineFromContext.machine_id_pk,
                lastLocation: machineFromContext.venue_name || 'Unknown Venue',
                lastStatus: machineFromContext.machine_status || 'Unknown Status',
                serviceHistorySummary: 'Service history summary not available.'
            });
        } catch (err) {
            log('intake_machine_details_failed', err);
            setFormError('Failed to load machine details.');
            setMachineData(null);
        } finally {
            setIsLoadingMachineData(false);
        }
    };
    
    const onDrop = useCallback(async (acceptedFiles) => {
        setPhotos(prev => [...prev, ...acceptedFiles]);
        setIsUploadingPhotos(true);
        setPhotoUploadErrors({});
        const newPaths = [];
        const errors = {};

        for (const file of acceptedFiles) {
            try {
                console.log(`Uploading ${file.name}...`);
                // Pass relative path without base URL prefix
                const result = await uploadImage('add_servicing_image', file); 
                
                if (result && result.success && result.file_name) {
                    console.log(`Upload success: ${result.file_name}`);
                    newPaths.push(result.file_name);
                } else {
                    console.error(`Upload failed for ${file.name}:`, result?.error || 'Unknown error');
                    errors[file.name] = result?.error || 'Upload failed';
                    log('intake_photo_upload_failed', {
                        fileName: file.name,
                        error: result?.error || 'Upload failed',
                    });
                }
            } catch (err) {
                console.error(`Upload error for ${file.name}:`, err);
                errors[file.name] = err.message || 'Network error or other issue';
                log('intake_photo_upload_exception', {
                    fileName: file.name,
                    message: err.message || String(err),
                });
            }
        }
        
        setUploadedPhotoPaths(prev => [...prev, ...newPaths]);
        setPhotoUploadErrors(prev => ({...prev, ...errors}));
        setIsUploadingPhotos(false);

    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
        onDrop, 
        accept: { 'image/*': [] }
    });

    const handleSubmit = async (event) => {
        if (!selectedMachine || !selectedWarehouse) {
            setFormError('Please select a Machine ID and Warehouse.');
            return;
        }
        if (isUploadingPhotos) {
            setFormError('Please wait for photos to finish uploading.');
            return;
        }

        setIsLoading(true);
        setFormError('');

        // Construct payload matching PHP backend
        const payload = {
            machine_id: selectedMachine?.pk,
            wh_id: selectedWarehouse,
            notes: conditionNotes,
            images: uploadedPhotoPaths
        };
        
        // Validate payload before sending
        if (!payload.machine_id || !payload.wh_id) {
            setFormError("Missing machine primary key or warehouse ID.");
            setIsLoading(false);
            return;
        }

        console.log('Submitting Intake Payload:', payload);

        try {
            // Pass relative path without base URL prefix
            const result = await req('post', 'intake_post', payload);
            console.log("Intake API Result:", result);

            // Assuming result indicates success, e.g., result.success or similar
            // TODO: Adjust this condition based on the actual API response structure
            if (result) { // Simplified check - adjust as needed!
                alert('Machine intake submitted successfully!');
                
                // Update the machine status and venue_id in the local context
                if (selectedMachine?.pk && selectedWarehouse) { // Ensure warehouse is selected
                    updateMachineStatus(selectedMachine.pk, 'servicing', selectedWarehouse);
                }

                // Clear form
                setSelectedMachine(null);
                setMachineData(null);
                setSelectedWarehouse('');
                setConditionNotes('');
                setPhotos([]);
                setUploadedPhotoPaths([]);
                setPhotoUploadErrors({});
            } else {
                setFormError('Intake submit failed. Please try again.');
                log('intake_submit_rejected', payload);
            }
        } catch (err) {
             console.error('Error submitting intake:', err);
             const errorMsg = err.message || 'Failed to submit intake form. Please try again.';
             setFormError(errorMsg);
             log('intake_submit_failed', { payload, message: err.message || String(err) });
        } finally {
             setIsLoading(false);
        }
    };

    // Format machines for Autocomplete and sort
    const machineOptions = [...machines]
        // .filter(m => m.machine_status === 'deployed') // <-- Ensure filter is removed/commented
        .sort((a, b) => {
            // Keep sorting logic
            const aIsWarehouse = a.venue_name?.toLowerCase().includes('wh'); 
            const bIsWarehouse = b.venue_name?.toLowerCase().includes('wh');
            if (aIsWarehouse && !bIsWarehouse) return -1; 
            if (!aIsWarehouse && bIsWarehouse) return 1;  
            if (a.serial_id < b.serial_id) return -1;
            if (a.serial_id > b.serial_id) return 1;
            return 0;
        })
        .map(m => ({
            // Modify label to include status if not 'deployed'
            label: `${m.serial_id} (${m.venue_name || 'Unknown Venue'})${m.machine_status !== 'deployed' ? ` - ${m.machine_status}` : ''}`,
            id: m.serial_id, 
            pk: m.machine_id_pk, 
            status: m.machine_status // Keep status for disabling logic
        }));

    // Find the full object for the selected machine from the context data
    const selectedMachineFullData = selectedMachine 
        ? machines.find(m => m.serial_id === selectedMachine.id) 
        : null;

    // Drawer Handlers - Updated
    const handleOpenDrawer = async (machinePkToFetch, label, contentType) => { // Added label parameter
        if (!machinePkToFetch) return;

        console.log(`Intake - Opening drawer for machine PK ${machinePkToFetch}, content: ${contentType}`);
        setDrawerMachinePk(machinePkToFetch);
        setDrawerMachineLabel(label); // Set the label
        setDrawerContent(contentType);
        setIsDrawerOpen(true);
        setIsLoadingDrawer(true);
        setDrawerData(null); // Clear previous data
    
        const endpoint = `get_machine_history?machine_id=${machinePkToFetch}`; 
            
        console.log(`Intake - Drawer fetching from: /api/warehouse/${endpoint}`);
        
        try {
            const data = await req('get', endpoint); 
            console.log("Intake - Drawer data received:", data);
            if (data !== null && data !== undefined) {
                 setDrawerData(data); 
            } else {
                 console.warn("Intake - Drawer data received null or undefined.");
                 setDrawerData({ error: `No ${contentType} data found or invalid response.` });
            }
        } catch (error) { 
            log(`Error fetching drawer ${contentType}:`, error); // Use log utility
            setDrawerData({ error: `Failed to load ${contentType}. Check network request.` });
        } finally {
            setIsLoadingDrawer(false);
        }
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setDrawerMachinePk(null);
        setDrawerContent(null);
        setDrawerData(null);
        setIsLoadingDrawer(false); // Ensure loading state is reset
        setDrawerMachineLabel(''); // Reset label
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Machine Intake
            </Typography>

            {/* --- Step 1: Select Machine and Warehouse --- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    {/* --- Warehouse Selection --- */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required error={Boolean(warehouseError) || (!selectedWarehouse && Boolean(formError))}> 
                            <InputLabel id="warehouse-select-label">Receiving Warehouse</InputLabel>
                            <Select
                                labelId="warehouse-select-label"
                                id="warehouse-select"
                                value={selectedWarehouse}
                                label="Receiving Warehouse"
                                onChange={handleWarehouseChange}
                                disabled={isLoadingWarehouses}
                                displayEmpty
                            >
                                {isLoadingWarehouses && <MenuItem value=""><CircularProgress size={20} /></MenuItem>}
                                {!isLoadingWarehouses && (
                                    <MenuItem value="">
                                        <em>Select warehouse</em>
                                    </MenuItem>
                                )}
                                {warehouses.map((wh) => (
                                    <MenuItem key={wh.venue_id} value={String(wh.venue_id)}>
                                        {wh.venue_name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {warehouseError && <Typography color="error" variant="caption">Error loading warehouses</Typography>}
                        </FormControl>
                    </Grid>

                    {/* --- Machine Selection --- */}
                    <Grid item xs={12} sm={6}>
                         {isLoadingMachines && <CircularProgress />} 
                         {machineError && !isLoadingMachines && (
                             <Typography color="error">Error loading machine list: {machineError}</Typography>
                         )}
                         {!isLoadingMachines && !machineError && (
                             <Autocomplete
                                id="machine-select"
                                options={machineOptions}
                                getOptionLabel={(option) => option.label || ""}
                                value={selectedMachine}
                                onChange={handleMachineSelect}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Machine ID" required error={!selectedMachine && formError} />
                                )}
                                // Disable option if status is not 'deployed' or if it's already in a warehouse?
                                // getOptionDisabled={(option) => option.status !== 'deployed'} 
                            />
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* --- Step 2: Machine Details & Intake Form (Conditional) --- */}
            {selectedMachine && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    {isLoadingMachineData ? (
                        <Box sx={{ textAlign: 'center' }}><CircularProgress /></Box>
                    ) : machineData ? (
                        <> 
                            {/* Display Machine Info & History Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Intake for: {machineData.id} ({machineData.lastLocation})</Typography>
                                {/* History Icon Button */}
                                <Tooltip title="View History">
                                    <IconButton 
                                        onClick={() => handleOpenDrawer(machineData.pk, machineData.id, 'history')} // Pass PK, label, and type
                                        size="small"
                                        color="primary"
                                    >
                                        <HistoryIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {/* Rest of the intake form... */}
                            <Grid container spacing={3}>
                                {/* Condition Notes */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="conditionNotes"
                                        label="Condition Notes"
                                        multiline
                                        rows={4}
                                        value={conditionNotes}
                                        onChange={(e) => setConditionNotes(e.target.value)}
                                    />
                                </Grid>

                                {/* Photo Upload */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" gutterBottom>Upload Photos</Typography>
                                    <Box {...getRootProps()} sx={{ border: '2px dashed grey', borderRadius: 1, p: 2, textAlign: 'center', cursor: 'pointer', bgcolor: isDragActive ? 'action.hover' : 'background.paper' }}>
                                        <input {...getInputProps()} />
                                        <p>{isDragActive ? 'Drop photos here...' : 'Drag \'n\' drop photos, or click to select'}</p>
                                    </Box>
                                    {/* Display Uploaded Photos & Errors */}
                                    {(photos.length > 0 || Object.keys(photoUploadErrors).length > 0 || isUploadingPhotos) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Files:</Typography>
                                            <List dense>
                                                {uploadedPhotoPaths.map((path, index) => (
                                                    <ListItem key={`uploaded-${index}-${path}`}>
                                                         <ListItemText primary={path.split('/').pop() || path} secondary="Uploaded" secondaryTypographyProps={{ color: 'success.main' }}/>
                                                         {/* Add delete button maybe? */}
                                                    </ListItem>
                                                ))}
                                                {photos.filter(f => !uploadedPhotoPaths.some(up => up.includes(f.name))).map((file, index) => (
                                                    <ListItem key={`pending-${index}-${file.name}`}>
                                                        <ListItemText 
                                                            primary={file.name} 
                                                            secondary={photoUploadErrors[file.name] ? `Error: ${photoUploadErrors[file.name]}` : (isUploadingPhotos ? 'Uploading...' : 'Pending')} 
                                                            secondaryTypographyProps={{ color: photoUploadErrors[file.name] ? 'error' : 'text.secondary' }}
                                                        />
                                                        {isUploadingPhotos && !photoUploadErrors[file.name] && <CircularProgress size={20} sx={{ ml: 1 }}/>}
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="persiangreen"
                                        onClick={handleSubmit}
                                        disabled={isLoading || isUploadingPhotos || !selectedWarehouse || !selectedMachine}
                                        fullWidth
                                        size="large"
                                    >
                                        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Complete Intake'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                         <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>Loading machine data...</Typography>
                    )}
                </Paper>
            )}

            {/* Show general form error */}
            {formError && (
                 <Typography color="error" sx={{ mt: 2 }}>{formError}</Typography>
             )}

             {/* Render Drawer */} 
             {drawerMachinePk && (
                 <MachineHistoryDrawer 
                     open={isDrawerOpen}
                     onClose={handleCloseDrawer}
                     machinePk={drawerMachinePk}
                     contentType={drawerContent}
                     data={drawerData}
                     loading={isLoadingDrawer}
                     machineLabel={drawerMachineLabel} // Use state for label
                 />
            )}

        </Container>
    );
} 